import { useState } from 'react';
import { Segmented, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyExportStockXlsxQuery, useStockReportQuery } from '@/entities/reports';
import type { StockReportRowAggregated, StockReportRowByBatch } from '@/entities/reports';
import { ReportPage } from '@/shared/ui';
import { formatDate } from '@/shared/lib/date';

/** §13, §6.2: остатки — К/Р/всего/себестоимость, переключатель «по партиям». */
export function StockReportPage() {
  const [byBatch, setByBatch] = useState(false);
  const { data, isFetching } = useStockReportQuery({ byBatch }, { pollingInterval: 30_000 });
  const [exportXlsx] = useLazyExportStockXlsxQuery();

  const aggregatedColumns: ColumnsType<StockReportRowAggregated> = [
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Коробок', dataIndex: 'sealedBoxes', width: 90, align: 'right' },
    { title: 'Россыпь', dataIndex: 'loosePcs', width: 90, align: 'right' },
    { title: 'Всего, шт', dataIndex: 'qtyTotal', width: 100, align: 'right' },
    { title: 'Себестоимость остатка', dataIndex: 'costValue', width: 160, align: 'right' },
    {
      title: '',
      width: 140,
      render: (_v, r) => (
        <>
          {r.hasProvisional ? <Tag color="warning">предварительная</Tag> : null}
          {r.recalcPending ? <Tag color="processing">пересчитывается</Tag> : null}
        </>
      ),
    },
  ];

  const byBatchColumns: ColumnsType<StockReportRowByBatch> = [
    { title: 'Товар', dataIndex: 'categoryName' },
    { title: 'Партия', dataIndex: 'source', width: 130 },
    { title: 'Дата прихода', dataIndex: 'receivedDate', width: 120, render: (v: string) => formatDate(v) },
    { title: 'Срок годности', dataIndex: 'expiryDate', width: 120, render: (v: string | null) => (v ? formatDate(v) : '—') },
    { title: 'Остаток, шт', dataIndex: 'qtyRemaining', width: 110, align: 'right' },
    { title: 'Себестоимость ед.', dataIndex: 'unitCost', width: 140, align: 'right' },
  ];

  return (
    <ReportPage
      title="Остатки"
      filters={
        <Segmented
          value={byBatch ? 'batch' : 'category'}
          onChange={(v) => setByBatch(v === 'batch')}
          options={[
            { value: 'category', label: 'По категориям' },
            { value: 'batch', label: 'По партиям' },
          ]}
        />
      }
      columns={byBatch ? (byBatchColumns as ColumnsType<StockReportRowAggregated | StockReportRowByBatch>) : (aggregatedColumns as ColumnsType<StockReportRowAggregated | StockReportRowByBatch>)}
      data={data?.rows ?? []}
      loading={isFetching}
      rowKey={(r) => ('batchId' in r ? r.batchId : r.categoryId)}
      onExport={() => exportXlsx({ byBatch }).unwrap()}
      exportFilename="Остатки.xlsx"
    />
  );
}
